<template>
  <div class="app" id="customerLayout">
    <t-navbar
      theme="customer"
      :user="authUser"
      :nav-items="this.$getPrimaryNavItems('customer')"
      v-model="closed"
    >
      <template #header>
        <div v-if="!loading">
          <t-nav-select
            v-if="navSelectBasedRole"
            label="Customers"
            data-test-id="navbar-customer-select"
            theme="customer"
            @selected="customerSelected"
            :closed="closed"
            :options="userAccountList"
            :initial-selected="initialCustomer"
          />
        </div>
        <b-skeleton
          v-else
          class="mt-6"
          animation="wave"
          height="56px"
          width="100%"
        />
      </template>
    </t-navbar>
    <div
      class="app-content"
      v-if="this.selectedUser"
      :key="this.selectedUser.id"
    >
      <main>
        <b-container fluid="lg">
          <t-header
            :user="authUser"
            :user-links="userLinks"
            :product-links="productLinks"
            :version="version"
            :license="license"
            theme="customer"
          />
          <router-view />
        </b-container>
      </main>
      <t-footer
        class="footer-image-bg"
        :version="appVersion"
        :logo="footerLogo"
        :nameservers="nameservers"
      />
      <t-modal />
      <t-toast />
      <t-alert />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { version } from "../../package";
import {
  PRODUCTS_ICON,
  PRODUCTS_TITLE,
  PRODUCTS_DESCRIPTION,
} from "@/constants";

import TNavbar from "@/components/TNavbar";
import TModal from "@/components/TModal";

export default {
  components: {
    TNavbar,
    TModal,
  },
  data() {
    return {
      version: null,
      license: null,
      initialCustomer: null,
      userAccountList: null,
      userRole: null,
      appVersion: version,
      closed: false,
      loading: true,
      nameservers: [],
      productLinks: [
        {
          title: PRODUCTS_TITLE.WEB,
          link: "#",
          active: true,
          icon: PRODUCTS_ICON.WEB,
          description: this.$t(PRODUCTS_DESCRIPTION.WEB),
          versionName: "- Errigal",
        },
      ],
      userLinks: [],
      footerLogo:
        this.$userPrefs.getCurrentUi().logo ||
        require("@/assets/branding/titanhq-logo.svg"),
    };
  },
  computed: {
    ...mapGetters("authentication", [
      "selectedUser",
      "authUser",
      "role",
      "mspId",
    ]),
    ...mapGetters("users", ["userAccounts"]),
    ...mapGetters("support", ["support"]),
    ...mapGetters("userInterface", ["interface"]),

    navSelectBasedRole() {
      return this.$userPrefs.getUserRole() === 4 ? false : true;
    },
  },
  watch: {
    userAccountList() {
      this.setSelectedUser();
    },
    userAccounts() {
      this.userAccountList = this.cleanCustomerAccounts(this.userAccounts);
    },
    support() {
      this.setSupportLinks(this.support);
    },
  },
  methods: {
    ...mapActions("categories", ["clearCache"]),
    ...mapActions("users", ["fetchCustomerAccounts", "fetchCustomerAccount"]),
    ...mapActions("support", ["fetchSupport"]),
    ...mapActions("authentication", ["setUser"]),
    ...mapActions("userInterface", ["fetchInterfaceBatch"]),
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),

    toggleNavigation() {
      this.closed = !this.closed;
    },
    onWindowResize(matchMedia) {
      if (matchMedia.matches && this.closed !== true) {
        this.closed = true;
      }

      if (!matchMedia.matches && this.closed !== false) {
        this.closed = false;
      }
    },
    customerSelected(customer) {
      this.clearCache();
      this.setUser(customer);
      localStorage.setItem("selectedCustomer", customer.id);
      if (customer.allCustomers) {
        this.$router.push("/msp/overview", null, { shallow: true });
      } else if (this.$route.path !== "/customer/overview") {
        this.$router.push("/customer/overview", null, { shallow: true });
      }
    },
    setSelectedUser() {
      if (this.selectedUser?.id) {
        this.initialCustomer = this.selectedUser.id;
        return;
      }

      const storedSelectedCustomerID =
        localStorage.getItem("selectedCustomer") || null;
      const filteredUserList = this.userAccountList.filter(
        (user) => user.id === parseInt(storedSelectedCustomerID)
      );

      if (storedSelectedCustomerID && filteredUserList.length) {
        this.setUser(filteredUserList[0]);
        this.initialCustomer = storedSelectedCustomerID;
      } else {
        this.setUser(this.userAccountList[0]);
        this.initialCustomer = this.userAccountList[0].id;
        localStorage.setItem("selectedCustomer", this.initialCustomer.id);
      }
    },
    cleanCustomerAccounts(accounts) {
      const allAccounts = accounts
        .map((acct) => {
          return {
            ...acct,
            name: acct.accountname,
            testID: `navbar-customer-select-${acct.accountname
              .toLowerCase()
              .replace(/\s/g, "-")}`,
            allCustomers: false,
            short: this.$getAccountShortName(acct.accountname),
          };
        })
        .sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : b.name.toLowerCase() > a.name.toLowerCase()
            ? -1
            : 0
        );

      return [
        { name: "All Customers", short: "AC", id: 0, allCustomers: true },
        ...allAccounts,
      ];
    },
    setSupportLinks(support) {
      this.userLinks = [
        {
          title: this.$t("Security"),
          link: "/profile/security",
          icon: "gear",
        },
      ];

      if (support.link) {
        this.userLinks.push({
          title: this.$t("Support"),
          link: this.support.link,
          icon: "life-preserver",
        });
      }

      if (support.email) {
        this.userLinks.push({
          title: this.$t("Contact"),
          link: `mailto:${this.support.email}`,
          icon: "chat-left",
        });
      }
    },
  },
  beforeMount() {
    this.setPageTitle("");
    this.setPageSubtitle([]);
  },
  beforeCreate() {
    const userToken = this.$userPrefs.getCurrentToken();
    if (!userToken) this.$router.push("/").catch(() => {});
    this.userRole = this.$userPrefs.getUserRole();
    this.$http.setAuthorisationToken(userToken);
  },
  async created() {
    if (!this.userAccounts.length && this.$userPrefs.getUserRole() !== 4) {
      await this.fetchCustomerAccounts({ msp_id: this.mspId });
    }
    await this.fetchSupport(false);
    this.setSupportLinks(this.support);
    this.userAccountList = this.cleanCustomerAccounts(this.userAccounts);
    this.loading = false;

    await this.fetchInterfaceBatch();
    // TODO: enable when the API returns the correct DNS resolvers
    // this.nameservers = this.interface.nameservers;
    this.version =
      this.interface.system.version !== undefined
        ? String(this.interface.system.version)
        : null;
    this.license =
      this.interface.system.license_number !== undefined
        ? this.interface.system.license_number
        : null;
  },
};
</script>

<style lang="scss" scoped>
.app {
  display: flex;
  height: 100vh;
  overflow: hidden;

  &-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    footer {
      margin-top: auto;
    }
  }

  .app-content {
    background-color: $bg-light;
  }
}

@media only screen and (max-width: 1200px) {
  .app {
    &-content {
      margin-left: 3.5rem;
    }
  }
}
</style>
